import { useEffect, useRef, useState, useMemo } from "react";
import { useForm } from "react-hook-form";
import {
  User,
  UserOrg,
  UserOrgForm,
  userMutationFunctions,
} from "@/models/user.tsx";
import Popup from "@/components/shared/Popup";
import { CoreDataModel } from "@/models";
import { LuCheck, LuEye, LuPencil, LuShield } from "react-icons/lu";
import {
  RECORD_TYPE_LABELS,
  ADDITIONAL_PERMISSION_LABELS,
} from "../hooks/useOrgPermissions";

const bulkBtnStyle = (active: boolean): React.CSSProperties => ({
  fontSize: "0.8rem",
  borderRadius: 6,
  padding: "5px 12px",
  border: `1px solid ${active ? "var(--color-p2)" : "#cbd5e1"}`,
  background: active ? "rgba(0,58,93,0.08)" : "#f8fafc",
  color: active ? "var(--color-p2)" : "#64748b",
  fontWeight: active ? 600 : 500,
  transition: "all 0.15s",
  cursor: "pointer",
});

export function AddNewOrgModal({
  show,
  onHide,
  user,
  leaid,
  coreData,
  entityResult,
  childrenResult,
}: {
  show: boolean;
  onHide: () => void;
  user: User<UserOrg>;
  leaid: string;
  coreData: CoreDataModel;
  entityResult: any;
  childrenResult: any;
}) {
  const entityGroup = [entityResult?.data, ...(childrenResult?.data ?? [])]
    .flat()
    .filter(Boolean);

  const securityGroups = new Set(
    entityGroup.map((x: any) => x.SecurityGroupKey)
  );
  const availableSecurityGroups = (coreData?.securityGroup ?? []).filter((x) =>
    securityGroups.has(x.ScrtyGrpKey)
  );
  const firstOrgType =
    availableSecurityGroups[0]?.ScrtyGrpKey?.toString() ?? "";

  const userMutateFns = userMutationFunctions(leaid, () => onHide());
  const { register, handleSubmit, reset, watch, setValue } =
    useForm<UserOrgForm>({
      defaultValues: { orgType: firstOrgType },
    });

  const orgTypeWatch = watch("orgType");

  const hasInitializedRef = useRef(false);

  const orgRoles = useMemo(
    () =>
      (coreData?.securityRoles ?? []).filter(
        (r) => r.ScrtyGrpKey?.toString() === orgTypeWatch?.toString()
      ),
    [coreData?.securityRoles, orgTypeWatch]
  );

  const recordTypes = useMemo(
    () =>
      [
        ...new Set(
          orgRoles
            .filter(
              (r) =>
                r.CAPSDACRoleCode.endsWith("View") ||
                r.CAPSDACRoleCode.endsWith("Edit")
            )
            .map((r) => r.CAPSDACRoleCode.replace(/View$|Edit$/, ""))
        ),
      ].sort((a, b) => {
        const order = Object.keys(RECORD_TYPE_LABELS);
        const ai = order.indexOf(a);
        const bi = order.indexOf(b);
        if (ai === -1 && bi === -1) return 0;
        if (ai === -1) return 1;
        if (bi === -1) return -1;
        return ai - bi;
      }),
    [orgRoles]
  );

  const additionalPermissions = useMemo(
    () =>
      orgRoles
        .filter(
          (r) =>
            !r.CAPSDACRoleCode.endsWith("View") &&
            !r.CAPSDACRoleCode.endsWith("Edit") &&
            r.CAPSDACRoleCode !== "ADMIN"
        )
        .map((r) => r.CAPSDACRoleCode),
    [orgRoles]
  );

  const adminRole = useMemo(
    () => orgRoles.find((r) => r.CAPSDACRoleCode === "ADMIN"),
    [orgRoles]
  );

  const codeToId = useMemo(
    () =>
      Object.fromEntries(
        (coreData?.securityRoles ?? []).map((r) => [
          r.CAPSDACRoleCode,
          r.CAPSDACRoleId,
        ])
      ),
    [coreData?.securityRoles]
  );

  const [isOrgAdmin, setIsOrgAdmin] = useState(false);
  const [viewPerms, setViewPerms] = useState<Record<string, boolean>>({});
  const [editPerms, setEditPerms] = useState<Record<string, boolean>>({});
  const [additionalPerms, setAdditionalPerms] = useState<
    Record<string, boolean>
  >({});

  useEffect(() => {
    setIsOrgAdmin(false);
    setViewPerms(Object.fromEntries(recordTypes.map((rt) => [rt, false])));
    setEditPerms(Object.fromEntries(recordTypes.map((rt) => [rt, false])));
    setAdditionalPerms(
      Object.fromEntries(additionalPermissions.map((p) => [p, false]))
    );
  }, [orgTypeWatch, recordTypes, additionalPermissions]);

  useEffect(() => {
    const roleIds: string[] = [];
    if (isOrgAdmin && adminRole)
      roleIds.push(adminRole.CAPSDACRoleId.toString());
    recordTypes.forEach((rt) => {
      if (viewPerms[rt] && codeToId[`${rt}View`])
        roleIds.push(codeToId[`${rt}View`].toString());
      if (editPerms[rt] && codeToId[`${rt}Edit`])
        roleIds.push(codeToId[`${rt}Edit`].toString());
    });
    additionalPermissions.forEach((p) => {
      if (additionalPerms[p] && codeToId[p])
        roleIds.push(codeToId[p].toString());
    });
    setValue("roleIds", roleIds);
  }, [
    isOrgAdmin,
    viewPerms,
    editPerms,
    additionalPerms,
    adminRole,
    recordTypes,
    additionalPermissions,
    codeToId,
    setValue,
  ]);

  useEffect(() => {
    if (!show) {
      hasInitializedRef.current = false;
    }
  }, [show]);

  useEffect(() => {
    setValue("esiId", "");
    setValue("roleIds", []);
  }, [orgTypeWatch, setValue]);

  useEffect(() => {
    if (show && firstOrgType && !hasInitializedRef.current) {
      hasInitializedRef.current = true;
      setValue("orgType", firstOrgType);
    }
  }, [show, firstOrgType, setValue]);

  if (!coreData?.securityGroup || !coreData?.securityRoles) return null;

  const onSubmit = (x: UserOrgForm) => {
    x.esiIdPath = `${x.esiId !== "0" ? "0-" : ""}${x.esiId}-`;
    userMutateFns.handleUserOrgSave({ ...user, userOrgs: [x] });
  };

  const handleClose = () => {
    reset({ orgType: firstOrgType });
    setIsOrgAdmin(false);
    setViewPerms(Object.fromEntries(recordTypes.map((rt) => [rt, false])));
    setEditPerms(Object.fromEntries(recordTypes.map((rt) => [rt, false])));
    setAdditionalPerms(
      Object.fromEntries(additionalPermissions.map((p) => [p, false]))
    );
    onHide();
  };

  const toggle = (type: "view" | "edit", rt: string) => {
    if (type === "edit") {
      setEditPerms((prev) => {
        const next = !prev[rt];
        if (next) setViewPerms((vp) => ({ ...vp, [rt]: true }));
        return { ...prev, [rt]: next };
      });
    } else {
      setViewPerms((prev) => {
        const next = !prev[rt];
        if (!next) setEditPerms((ep) => ({ ...ep, [rt]: false }));
        return { ...prev, [rt]: next };
      });
    }
  };

  const allView = recordTypes.every((rt) => viewPerms[rt]);
  const allEdit = recordTypes.every((rt) => editPerms[rt]);
  const allAdditional = additionalPermissions.every((p) => additionalPerms[p]);
  const allFullAccess = allView && allEdit && allAdditional;

  const setAllView = () => {
    setViewPerms(Object.fromEntries(recordTypes.map((rt) => [rt, !allView])));
    if (allView)
      setEditPerms(Object.fromEntries(recordTypes.map((rt) => [rt, false])));
  };

  const setAllEdit = () => {
    setEditPerms(Object.fromEntries(recordTypes.map((rt) => [rt, !allEdit])));
    if (!allEdit)
      setViewPerms(Object.fromEntries(recordTypes.map((rt) => [rt, true])));
  };

  const setFullAccess = () => {
    setViewPerms(
      Object.fromEntries(recordTypes.map((rt) => [rt, !allFullAccess]))
    );
    setEditPerms(
      Object.fromEntries(recordTypes.map((rt) => [rt, !allFullAccess]))
    );
    setAdditionalPerms(
      Object.fromEntries(additionalPermissions.map((p) => [p, !allFullAccess]))
    );
  };

  const setAllAdditional = () => {
    setAdditionalPerms(
      Object.fromEntries(additionalPermissions.map((p) => [p, !allAdditional]))
    );
  };

  const getDomainLabel = (rt: string) => {
    const label = RECORD_TYPE_LABELS[rt];
    return label ? `${label} (${rt})` : rt;
  };

  const getPermissionLabel = (p: string) =>
    ADDITIONAL_PERMISSION_LABELS[p] ?? p;

  return (
    <Popup
      show={show}
      header="Grant Permissions to New Organization"
      onClose={handleClose}
      submitFormId="new-org-form"
      submitButtonText="Save"
      submitButtonLoading={userMutateFns.mutationState.isPending}
      submitButtonLoadingText="Saving..."
      hasButton={false}
    >
      <form onSubmit={handleSubmit(onSubmit)} id="new-org-form">
        <div
          className="row g-3"
          style={{
            background: "#fff",
            margin: "-1rem -1rem -1rem",
            padding: "1rem 1rem 2rem",
          }}
        >
          <div className="col-12 col-md-6">
            <label className="form-label fw-semibold small">Org Type</label>
            <select
              className="form-select"
              style={{ cursor: "pointer" }}
              {...register("orgType", { required: true })}
            >
              {availableSecurityGroups.map((x) => (
                <option key={x.ScrtyGrpKey} value={x.ScrtyGrpKey}>
                  {x.ScrtyGrpName}
                </option>
              ))}
            </select>
          </div>

          <div className="col-12 col-md-6">
            <label className="form-label fw-semibold small">
              Organization Name
            </label>
            <select
              className="form-select"
              style={{ cursor: "pointer" }}
              {...register("esiId", { required: true })}
            >
              {entityGroup
                .filter(
                  (x: any) =>
                    x.SecurityGroupKey?.toString() === orgTypeWatch?.toString()
                )
                .map((x: any) => (
                  <option key={x.LEAID} value={x.LEAID}>
                    {x.LEAName}
                  </option>
                ))}
            </select>
          </div>

          {adminRole && (
            <div className="col-12">
              <label
                className="d-flex align-items-center gap-3 p-3 mb-0"
                style={{
                  border: "1px solid #e2e8f0",
                  borderRadius: 8,
                  background: isOrgAdmin ? "rgba(0,58,93,0.03)" : "#fff",
                  cursor: "pointer",
                  flex: 1,
                }}
              >
                <input
                  type="checkbox"
                  checked={isOrgAdmin}
                  onChange={() => setIsOrgAdmin((v) => !v)}
                  style={{
                    width: 15,
                    height: 15,
                    accentColor: "var(--color-p2)",
                    flexShrink: 0,
                    cursor: "pointer",
                  }}
                />
                <LuShield
                  size={16}
                  style={{
                    color: isOrgAdmin ? "var(--color-p2)" : "#94a3b8",
                    flexShrink: 0,
                  }}
                />
                <div>
                  <div
                    className="fw-semibold"
                    style={{
                      fontSize: "0.9rem",
                      color: isOrgAdmin ? "var(--color-p2)" : "#1e293b",
                    }}
                  >
                    Administrator
                  </div>
                  <div className="text-muted" style={{ fontSize: "0.8rem" }}>
                    {isOrgAdmin
                      ? "Full administrative privileges enabled"
                      : "Grant full admin access"}
                  </div>
                </div>
              </label>
            </div>
          )}

          {recordTypes.length > 0 && (
            <div className="col-12">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <span
                  className="fw-semibold"
                  style={{
                    fontSize: "0.85rem",
                    letterSpacing: "0.07em",
                    textTransform: "uppercase",
                    color: "#475569",
                  }}
                >
                  Data Access
                </span>
                <div className="d-flex gap-2">
                  <button
                    type="button"
                    className="btn btn-sm d-flex align-items-center gap-1"
                    style={bulkBtnStyle(allFullAccess)}
                    onClick={setFullAccess}
                  >
                    <LuCheck size={11} /> Full Access
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm d-flex align-items-center gap-1"
                    style={bulkBtnStyle(allEdit)}
                    onClick={setAllEdit}
                  >
                    <LuPencil size={11} /> All Edit
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm d-flex align-items-center gap-1"
                    style={bulkBtnStyle(allView)}
                    onClick={setAllView}
                  >
                    <LuEye size={11} /> All View
                  </button>
                  {additionalPermissions.length > 0 && (
                    <button
                      type="button"
                      className="btn btn-sm d-flex align-items-center gap-1"
                      style={bulkBtnStyle(allAdditional)}
                      onClick={setAllAdditional}
                    >
                      <LuCheck size={11} /> All Additional
                    </button>
                  )}
                </div>
              </div>

              <div className="row g-2">
                {recordTypes.map((rt) => (
                  <div key={rt} className="col-12 col-md-6">
                    <div
                      className="d-flex align-items-center justify-content-between px-3"
                      style={{
                        border: "1px solid #e2e8f0",
                        borderRadius: 8,
                        background: "#fff",
                        height: 40,
                      }}
                    >
                      <span
                        className="fw-semibold"
                        style={{ fontSize: "0.85rem", color: "#1e293b" }}
                      >
                        {getDomainLabel(rt)}
                      </span>
                      <div className="d-flex gap-2 align-items-center">
                        {(["view", "edit"] as const).map((type) => {
                          const active =
                            type === "view" ? viewPerms[rt] : editPerms[rt];
                          return (
                            <label
                              key={type}
                              className="d-flex align-items-center gap-1 mb-0"
                              style={{
                                fontSize: "0.82rem",
                                color: active ? "var(--color-p2)" : "#64748b",
                                cursor: "pointer",
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={!!active}
                                onChange={() => toggle(type, rt)}
                                style={{
                                  accentColor: "var(--color-p2)",
                                  width: 15,
                                  height: 15,
                                  cursor: "pointer",
                                }}
                              />
                              {type.charAt(0).toUpperCase() + type.slice(1)}
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {additionalPermissions.length > 0 && (
            <div className="col-12">
              <div
                className="fw-semibold mb-2"
                style={{
                  fontSize: "0.85rem",
                  letterSpacing: "0.07em",
                  textTransform: "uppercase",
                  color: "#475569",
                }}
              >
                Additional Permissions
              </div>
              <div className="d-flex flex-wrap gap-2">
                {additionalPermissions.map((p) => {
                  const active = additionalPerms[p];
                  return (
                    <button
                      key={p}
                      type="button"
                      className="btn d-flex align-items-center gap-2"
                      style={{
                        fontSize: "0.85rem",
                        fontWeight: active ? 600 : 400,
                        borderRadius: 999,
                        padding: "6px 20px",
                        border: `1.5px solid ${active ? "var(--color-p2)" : "#94a3b8"}`,
                        background: active ? "rgba(0,58,93,0.08)" : "#fff",
                        color: active ? "var(--color-p2)" : "#475569",
                        transition: "all 0.15s",
                        lineHeight: 1.4,
                      }}
                      onClick={() =>
                        setAdditionalPerms((prev) => ({
                          ...prev,
                          [p]: !prev[p],
                        }))
                      }
                    >
                      {active && <LuCheck size={13} />}
                      {getPermissionLabel(p)}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </form>
    </Popup>
  );
}
